import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Textarea, Pill } from "../components/UI";
import { toast } from "sonner";
import { Plus, Sparkles, Send, Trash2, RefreshCw, Globe, Users, Loader2, CheckCircle2, CalendarClock } from "lucide-react";

const EMAIL_LINE_RE = /([^@\s,;<>"']+@[^@\s,;<>"']+\.[^@\s,;<>"']+)/;

const parseCsv = (textContent) => {
  const contacts = [];
  for (const raw of textContent.split(/\r?\n/)) {
    const m = raw.match(EMAIL_LINE_RE);
    if (!m) continue;
    const email = m[1].toLowerCase();
    const name = raw.replace(m[1], "").replace(/[",;]+/g, " ").trim();
    contacts.push({ email, name });
  }
  return contacts;
};

const STATUS_PILL = { sent: "Active", scheduled: "Pending", sending: "Pending", failed: "Error" };

const EMPTY = {
  name: "", entity_id: "", subject: "", preheader: "", from_name: "", from_local: "team",
  domain: "", list_id: "", headline: "", body: "", cta_label: "", cta_url: "",
  disclaimer: "", brand_color: "#22d3ee",
};

export default function EmailCampaigns() {
  const [tab, setTab] = useState("campaigns");
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [domains, setDomains] = useState([]);
  const [defaultDomain, setDefaultDomain] = useState("");
  const [entities, setEntities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState("");
  const [testTo, setTestTo] = useState("");
  const [schedAt, setSchedAt] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [reports, setReports] = useState({});

  const [newDomain, setNewDomain] = useState("");
  const [domainSetup, setDomainSetup] = useState(null);
  const [listName, setListName] = useState("");
  const [listContacts, setListContacts] = useState([]);
  const [consent, setConsent] = useState(false);

  const load = async () => {
    api.get("/email/campaigns").then(r => setCampaigns(r.data)).catch(() => {});
    api.get("/email/lists").then(r => setLists(r.data)).catch(() => {});
    api.get("/email/domains").then(r => { setDomains(r.data.domains || []); setDefaultDomain(r.data.default_domain || ""); }).catch(() => {});
    api.get("/political-entities").then(r => setEntities(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const err = (e, fallback) => toast.error(errMsg(e, fallback));

  const set = (k, v) => setEditing(f => ({ ...f, [k]: v }));

  const openEditor = (camp) => {
    setEditing(camp ? { ...camp } : { ...EMPTY });
    setPreviewHtml("");
    setSchedAt("");
  };

  const saveDraft = async (silent = false) => {
    const payload = { ...editing };
    let saved;
    try {
      if (editing.id) saved = (await api.put(`/email/campaigns/${editing.id}`, payload)).data;
      else saved = (await api.post("/email/campaigns", payload)).data;
      setEditing(saved);
      if (!silent) toast.success("Draft saved");
      load();
      return saved;
    } catch (e) { err(e, "Could not save"); return null; }
  };

  const refreshPreview = async () => {
    const saved = await saveDraft(true);
    if (!saved) return;
    try {
      const { data } = await api.get(`/email/campaigns/${saved.id}/preview`);
      setPreviewHtml(data.html);
    } catch (e) { err(e, "Preview failed"); }
  };

  const aiDraft = async () => {
    const saved = await saveDraft(true);
    if (!saved) return;
    setBusy("ai");
    try {
      const { data } = await api.post(`/email/campaigns/${saved.id}/ai-draft`, { instructions: aiPrompt });
      setEditing(data);
      toast.success("AI draft ready — review and edit");
    } catch (e) { err(e, "AI draft failed"); }
    finally { setBusy(""); }
  };

  const testSend = async () => {
    const saved = await saveDraft(true);
    if (!saved) return;
    setBusy("test");
    try {
      const { data } = await api.post(`/email/campaigns/${saved.id}/test-send`, { to: testTo });
      toast.success(`Test sent from ${data.domain}`);
    } catch (e) { err(e, "Test send failed"); }
    finally { setBusy(""); }
  };

  const sendCampaign = async () => {
    const saved = await saveDraft(true);
    if (!saved) return;
    const lst = lists.find(l => l.id === saved.list_id);
    if (!window.confirm(`Send "${saved.subject}" to ${lst ? lst.count : "?"} recipients now?`)) return;
    setBusy("send");
    try {
      const { data } = await api.post(`/email/campaigns/${saved.id}/send`);
      toast.success(`Sent to ${data.sent} recipients via ${data.domain}`);
      setEditing(null);
      load();
    } catch (e) { err(e, "Send failed"); }
    finally { setBusy(""); }
  };

  const scheduleCampaign = async () => {
    if (!schedAt) { toast.error("Pick a date and time first"); return; }
    const saved = await saveDraft(true);
    if (!saved) return;
    setBusy("schedule");
    try {
      await api.post(`/email/campaigns/${saved.id}/schedule`, { scheduled_at: new Date(schedAt).toISOString() });
      toast.success("Campaign scheduled");
      setEditing(null);
      load();
    } catch (e) { err(e, "Could not schedule"); }
    finally { setBusy(""); }
  };

  const cancelSchedule = async (cid) => {
    try {
      await api.post(`/email/campaigns/${cid}/cancel-schedule`);
      toast.success("Schedule cancelled — back to draft");
      load();
    } catch (e) { err(e, "Could not cancel schedule"); }
  };

  const fetchReport = async (cid) => {
    setReports(r => ({ ...r, [cid]: { loading: true } }));
    try {
      const { data } = await api.get(`/email/campaigns/${cid}/report`);
      setReports(r => ({ ...r, [cid]: data.stats || {} }));
    } catch { setReports(r => ({ ...r, [cid]: {} })); }
  };

  const addDomain = async () => {
    setBusy("domain");
    try {
      const { data } = await api.post("/email/domains", { domain: newDomain });
      setDomainSetup(data);
      load();
    } catch (e) { err(e, "Could not add domain"); }
    finally { setBusy(""); }
  };

  const verifyDomain = async (domain) => {
    setBusy("verify");
    try {
      const { data } = await api.post("/email/domains/verify", { domain });
      setDomainSetup(data);
      toast[data.state === "active" ? "success" : "info"](`Domain ${domain}: ${data.state}`);
      load();
    } catch (e) { err(e, "Verify failed"); }
    finally { setBusy(""); }
  };

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setListContacts(parseCsv(String(reader.result || "")));
    reader.readAsText(file);
  };

  const createList = async () => {
    setBusy("list");
    try {
      await api.post("/email/lists", { name: listName, contacts: listContacts, consent_confirmed: consent });
      toast.success("List created");
      setListName(""); setListContacts([]); setConsent(false);
      load();
    } catch (e) { err(e, "Could not create list"); }
    finally { setBusy(""); }
  };

  const activeDomains = domains.filter(d => d.state === "active").map(d => d.domain);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Email Channel"
        title="Email Campaigns"
        subtitle="Design, send from your own authenticated domain, and track opens and clicks — email as a first-class channel."
        action={!editing && <Btn onClick={() => openEditor(null)} data-testid="new-email-campaign-btn"><Plus className="w-4 h-4" /> New email</Btn>}
      />

      {!editing && (
        <div className="flex gap-1 border-b border-white/10" data-testid="email-tabs">
          {["campaigns", "lists", "domains"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`email-tab-${t}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider font-medium border-b-2 transition-colors ${tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* ---------- Editor ---------- */}
      {editing && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card data-testid="email-editor">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{editing.id ? "Edit email" : "New email"}</h3>
              <button onClick={() => setEditing(null)} className="text-xs text-slate-400 hover:text-white">← Back to list</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Internal name" value={editing.name} onChange={e => set("name", e.target.value)} className="col-span-2" data-testid="email-name-input" />
              <Select label="Committee (optional)" value={editing.entity_id || ""} onChange={e => {
                const ent = entities.find(x => x.id === e.target.value);
                setEditing(f => ({ ...f, entity_id: e.target.value, disclaimer: f.disclaimer || (ent?.approved_disclaimers?.[0] || "") }));
              }}>
                <option value="">None (non-political)</option>
                {entities.map(en => <option key={en.id} value={en.id}>{en.public_campaign_name || en.legal_org_name}</option>)}
              </Select>
              <Select label="Audience list" value={editing.list_id || ""} onChange={e => set("list_id", e.target.value)} data-testid="email-list-select">
                <option value="">Select list…</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
              </Select>
              <Input label="Subject" value={editing.subject} onChange={e => set("subject", e.target.value)} className="col-span-2" data-testid="email-subject-input" />
              <Input label="Preheader (inbox preview text)" value={editing.preheader} onChange={e => set("preheader", e.target.value)} className="col-span-2" />
              <Input label="From name" value={editing.from_name} onChange={e => set("from_name", e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Input label="From address" value={editing.from_local} onChange={e => set("from_local", e.target.value.replace(/[^a-z0-9._-]/gi, ""))} />
                <Select label="Domain" value={editing.domain || ""} onChange={e => set("domain", e.target.value)} data-testid="email-domain-select">
                  <option value="">{defaultDomain} (default)</option>
                  {activeDomains.map(d => <option key={d} value={d}>{d}</option>)}
                </Select>
              </div>
              <Input label="Headline" value={editing.headline} onChange={e => set("headline", e.target.value)} className="col-span-2" />
              <Textarea label="Body (blank line = new paragraph)" rows={7} value={editing.body} onChange={e => set("body", e.target.value)} className="col-span-2" data-testid="email-body-input" />
              <Input label="Button label" value={editing.cta_label} onChange={e => set("cta_label", e.target.value)} />
              <Input label="Button URL" value={editing.cta_url} onChange={e => set("cta_url", e.target.value)} />
              <Input label="Disclaimer (required for political)" value={editing.disclaimer} onChange={e => set("disclaimer", e.target.value)} className="col-span-2" data-testid="email-disclaimer-input" />
              <Input label="Brand color" type="color" value={editing.brand_color} onChange={e => set("brand_color", e.target.value)} />
            </div>

            <div className="mt-4 rounded-md border border-cyan-400/20 bg-cyan-400/5 p-3">
              <div className="flex gap-2">
                <input value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder="Optional: tone, topic, the ask…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-cyan-400/50" data-testid="email-ai-prompt" />
                <Btn onClick={aiDraft} disabled={busy === "ai"} data-testid="email-ai-draft-btn">
                  {busy === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI draft
                </Btn>
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5">AI writes subject, preheader, headline, body and CTA — grounded on the committee's brand kit.</div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Btn variant="secondary" onClick={() => saveDraft()} data-testid="email-save-draft-btn">Save draft</Btn>
              <Btn variant="secondary" onClick={refreshPreview} data-testid="email-preview-btn"><RefreshCw className="w-3.5 h-3.5" /> Preview</Btn>
              <div className="flex gap-1.5 items-center ml-auto">
                <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="test@you.com"
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs w-36 focus:outline-none focus:border-cyan-400/50" data-testid="email-test-to" />
                <Btn variant="secondary" onClick={testSend} disabled={busy === "test"} data-testid="email-test-send-btn">Test send</Btn>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2">
              <div className="flex gap-1.5 items-center">
                <input type="datetime-local" value={schedAt} onChange={e => setSchedAt(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-cyan-400/50" data-testid="email-schedule-at" />
                <Btn variant="secondary" onClick={scheduleCampaign} disabled={busy === "schedule"} data-testid="email-schedule-btn">
                  {busy === "schedule" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Schedule
                </Btn>
              </div>
              <Btn onClick={sendCampaign} disabled={busy === "send"} data-testid="email-send-btn" className="ml-auto">
                {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send now
              </Btn>
            </div>
            {editing.status === "scheduled" && editing.scheduled_at && (
              <div className="mt-2 text-xs text-amber-300">Scheduled for {editing.scheduled_at.slice(0, 16).replace("T", " ")} UTC</div>
            )}
          </Card>

          <Card padded={false} data-testid="email-preview-panel">
            <div className="px-5 py-3 border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">Live preview</div>
            {previewHtml
              ? <iframe title="email-preview" srcDoc={previewHtml} className="w-full bg-white" style={{ height: "640px", border: 0 }} />
              : <div className="p-10 text-center text-sm text-slate-500">Click Preview to render the email.</div>}
          </Card>
        </div>
      )}

      {/* ---------- Campaigns list ---------- */}
      {!editing && tab === "campaigns" && (
        <div className="space-y-3" data-testid="email-campaigns-list">
          {campaigns.map(c => {
            const rep = reports[c.id] || c.stats;
            return (
              <Card key={c.id}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-white">{c.name || c.subject}</span>
                      <Pill status={STATUS_PILL[c.status] || "Draft"}>{c.status}</Pill>
                      {c.status === "scheduled" && c.scheduled_at && (
                        <span className="text-[10px] text-amber-300 inline-flex items-center gap-1" data-testid={`email-scheduled-time-${c.id}`}>
                          <CalendarClock className="w-3 h-3" /> {c.scheduled_at.slice(0, 16).replace("T", " ")} UTC
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">{c.subject} · from {c.from_local}@{c.send_domain || c.domain || defaultDomain}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.status === "scheduled" && (
                      <Btn variant="secondary" onClick={() => cancelSchedule(c.id)} data-testid={`email-cancel-schedule-${c.id}`}>Cancel schedule</Btn>
                    )}
                    {c.status !== "sent" && <Btn variant="secondary" onClick={() => openEditor(c)} data-testid={`email-edit-${c.id}`}>Edit</Btn>}
                    {c.status === "sent" && (
                      <Btn variant="secondary" onClick={() => fetchReport(c.id)} data-testid={`email-report-btn-${c.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh stats
                      </Btn>
                    )}
                    <button onClick={async () => { if (window.confirm("Delete this email?")) { await api.delete(`/email/campaigns/${c.id}`); load(); } }}
                      className="text-slate-500 hover:text-rose-300 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {c.status === "sent" && (
                  <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-3 md:grid-cols-6 gap-3 text-center" data-testid={`email-stats-${c.id}`}>
                    {[["Sent", c.sent_count], ["Delivered", rep?.delivered], ["Opens", rep?.opened],
                      ["Clicks", rep?.clicked], ["Open rate", rep?.open_rate != null ? rep.open_rate + "%" : null],
                      ["Click rate", rep?.click_rate != null ? rep.click_rate + "%" : null]].map(([label, val]) => (
                      <div key={label}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                        <div className="font-display text-lg font-bold text-white">{rep?.loading ? "…" : (val ?? "—")}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
          {campaigns.length === 0 && <div className="text-center py-14 text-sm text-slate-500">No email campaigns yet — create your first one.</div>}
        </div>
      )}

      {/* ---------- Lists ---------- */}
      {!editing && tab === "lists" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="email-lists-tab">
          <Card>
            <h3 className="font-display text-lg font-bold mb-3">New audience list</h3>
            <Input label="List name" value={listName} onChange={e => setListName(e.target.value)} data-testid="email-list-name" />
            <div className="mt-3">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium">CSV upload (email, name)</label>
              <input type="file" accept=".csv,.txt" onChange={onCsvFile} data-testid="email-list-file"
                className="mt-1.5 block w-full text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-cyan-400/15 file:text-cyan-300 file:px-4 file:py-2 file:text-xs" />
              {listContacts.length > 0 && <div className="text-xs text-emerald-300 mt-2" data-testid="email-list-parsed">{listContacts.length} valid contacts parsed</div>}
            </div>
            <label className="flex items-start gap-2 mt-3 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 accent-cyan-400" data-testid="email-list-consent" />
              These contacts opted in to receive email from this campaign.
            </label>
            <div className="mt-4 flex gap-2">
              <Btn onClick={createList} disabled={busy === "list" || !listContacts.length} data-testid="email-list-create-btn">Create list</Btn>
              <Btn variant="secondary" onClick={async () => { try { await api.post("/email/lists/import-donors"); toast.success("Donor list imported"); load(); } catch (e) { err(e, "Import failed"); } }} data-testid="email-import-donors-btn">
                <Users className="w-3.5 h-3.5" /> Import donors
              </Btn>
            </div>
          </Card>
          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10 font-display font-bold">Your lists</div>
            <div className="divide-y divide-white/5">
              {lists.map(l => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between text-sm">
                  <div><span className="text-white">{l.name}</span> <span className="text-slate-500 text-xs">· {l.count} contacts · {l.source}</span></div>
                  <button onClick={async () => { await api.delete(`/email/lists/${l.id}`); load(); }} className="text-slate-500 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {lists.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">No lists yet.</div>}
            </div>
          </Card>
        </div>
      )}

      {/* ---------- Domains ---------- */}
      {!editing && tab === "domains" && (
        <div className="space-y-6" data-testid="email-domains-tab">
          <Card>
            <h3 className="font-display text-lg font-bold">Your sending domain</h3>
            <p className="text-xs text-slate-500 mt-1 mb-4">Authenticate your own domain with SPF, DKIM and DMARC so every email originates from your brand. Until it verifies, emails send from <span className="text-cyan-300">{defaultDomain}</span>.</p>
            <div className="flex gap-2 max-w-lg">
              <input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="mail.yourcampaign.com"
                className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400/50" data-testid="email-new-domain-input" />
              <Btn onClick={addDomain} disabled={busy === "domain"} data-testid="email-add-domain-btn"><Globe className="w-4 h-4" /> Add domain</Btn>
            </div>
            {domainSetup && (
              <div className="mt-5" data-testid="email-domain-dns">
                <div className="text-sm text-white mb-2">DNS records for <span className="text-cyan-300">{domainSetup.domain}</span> — state: <span className={domainSetup.state === "active" ? "text-emerald-300" : "text-amber-300"}>{domainSetup.state}</span></div>
                <div className="overflow-x-auto rounded-md border border-white/10">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-3 py-2">Type</th><th className="px-3 py-2">Host</th><th className="px-3 py-2">Value</th><th className="px-3 py-2">Status</th></tr></thead>
                    <tbody>
                      {(domainSetup.dns_records || []).map((r, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="px-3 py-2 font-mono text-cyan-300">{r.type}</td>
                          <td className="px-3 py-2 font-mono text-slate-300">{r.name}</td>
                          <td className="px-3 py-2 font-mono text-slate-400 break-all max-w-md">{r.value}</td>
                          <td className="px-3 py-2">{r.valid === "valid" ? <span className="text-emerald-300">valid</span> : <span className="text-amber-300">{r.valid || "pending"}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Btn className="mt-3" variant="secondary" onClick={() => verifyDomain(domainSetup.domain)} disabled={busy === "verify"} data-testid="email-verify-domain-btn">
                  {busy === "verify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Check verification
                </Btn>
              </div>
            )}
          </Card>
          <Card padded={false}>
            <div className="px-5 py-3 border-b border-white/10 font-display font-bold">Connected domains</div>
            <div className="divide-y divide-white/5">
              <div className="px-5 py-3 flex items-center justify-between text-sm">
                <span className="text-white">{defaultDomain} <span className="text-slate-500 text-xs">· platform default</span></span>
                <span className="text-emerald-300 text-xs">active</span>
              </div>
              {domains.map(d => (
                <div key={d.domain} className="px-5 py-3 flex items-center justify-between text-sm">
                  <span className="text-white">{d.domain}</span>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${d.state === "active" ? "text-emerald-300" : "text-amber-300"}`}>{d.state}</span>
                    <button onClick={() => setDomainSetup({ domain: d.domain, state: d.state, dns_records: d.dns_records })} className="text-xs text-cyan-300 hover:underline">DNS records</button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
